import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  runPartitionAssessment,
  enqueuePartitionConvert,
  getPartitionConversions,
} from "../../api/adminApi";

function formatBytes(n) {
  if (!n || n <= 0) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function formatNumber(n) {
  if (n === null || n === undefined) return "—";
  try { return Number(n).toLocaleString(); } catch { return String(n); }
}

// Map a PartitionConversions.Status to a badge color, reusing the shared lc-status-* palette.
function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "completed": return "lc-status-success";
    case "failed": return "lc-status-failed";
    case "running": return "lc-status-partial";
    case "blocked": return "lc-status-blocked";
    case "pending": return "sl-badge--tier";
    default: return ""; // Assessed / Ineligible — neutral
  }
}

// Per-database partition conversion: assess whether the physical table can be converted to a
// monthly DocumentDate-partitioned layout, enqueue an offline conversion, and show history.
// The actual rebuild runs in the background worker (opt-in) during a maintenance window.
export default function PartitioningPanel({ token, databaseId, databaseName }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await getPartitionConversions(token, databaseId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token, databaseId]);

  useEffect(() => { load(); }, [load]);

  const latest = rows[0] || null;
  const isPartitioned = useMemo(
    () => rows.some((r) => r.status === "Completed" || r.alreadyPartitioned),
    [rows]
  );
  const pending = useMemo(
    () => rows.find((r) => r.status === "Pending" || r.status === "Running") || null,
    [rows]
  );
  const canConvert = !!(latest && latest.eligible) && !isPartitioned && !pending;

  async function assess() {
    setBusy(true); setError(""); setInfo("");
    try {
      await runPartitionAssessment(token, databaseId);
      await load();
      setInfo(t("partitioning.assessedMsg"));
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function convert() {
    if (!window.confirm(t("partitioning.convertConfirm", { name: databaseName }))) return;
    setBusy(true); setError(""); setInfo("");
    try {
      const rec = await enqueuePartitionConvert(token, databaseId);
      await load();
      if (rec && (rec.status === "Pending" || rec.status === "Running")) {
        setInfo(t("partitioning.queuedMsg"));
      } else {
        setInfo(t("partitioning.notEligibleMsg", { reason: rec?.reason || rec?.status || "" }));
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function renderStatus() {
    if (isPartitioned) return <span className="sl-badge lc-status-success">{t("partitioning.partitioned")}</span>;
    if (pending) return (
      <span className={`sl-badge ${statusClass(pending.status)}`}>
        {pending.status === "Running" ? t("partitioning.converting") : t("partitioning.queued")}
      </span>
    );
    if (!latest) return <span className="sl-muted">{t("partitioning.notAssessed")}</span>;
    if (latest.eligible) return <span className="sl-badge">{t("partitioning.eligible")}</span>;
    if (latest.status === "Blocked") return <span className="sl-badge lc-status-blocked">{t("partitioning.blocked")}</span>;
    return <span className="sl-badge">{t("partitioning.ineligible")}</span>;
  }

  return (
    <div className="sl-bind-card" style={{ marginBottom: 16 }}>
      <div className="dbPropsSectionTitle" style={{ marginTop: 0 }}>{t("partitioning.title")}</div>
      <div className="sl-hint" style={{ marginTop: 4, marginBottom: 14, maxWidth: 620 }}>{t("partitioning.subtitle")}</div>

      {error ? <div className="arch-alert arch-alert--error" role="alert" style={{ marginBottom: 12 }}>{error}</div> : null}
      {info ? <div className="arch-alert arch-alert--success" style={{ marginBottom: 12 }}>{info}</div> : null}

      {/* Current state */}
      <div className="lc-inline" style={{ alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {renderStatus()}
        {latest && latest.reason && !isPartitioned && !pending ? (
          <span className="sl-muted" style={{ padding: 0 }}>{latest.reason}</span>
        ) : null}
      </div>

      {latest && latest.eligible && !isPartitioned && !pending ? (
        <div style={{ display: "flex", gap: 36, flexWrap: "wrap", margin: "14px 0 18px" }}>
          <Stat k={t("partitioning.rows")} v={formatNumber(latest.estimatedRowCount)} />
          <Stat k={t("partitioning.size")} v={formatBytes(latest.estimatedSizeBytes)} />
          <Stat k={t("partitioning.estDuration")} v={latest.estimatedMinutes ? t("partitioning.minutes", { n: latest.estimatedMinutes }) : "—"} />
        </div>
      ) : null}

      <div className="sl-form-actions" style={{ marginTop: 8, marginBottom: 4 }}>
        <button type="button" className="secondary-btn" disabled={busy} onClick={assess}>{t("partitioning.assess")}</button>
        <button type="button" className="primary-btn" disabled={busy || !canConvert} onClick={convert}>{t("partitioning.convert")}</button>
      </div>

      <div className="dbPropsSectionTitle" style={{ marginTop: 20 }}>{t("partitioning.history")}</div>
      {loading ? (
        <div className="sl-muted">{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="sl-muted">{t("partitioning.empty")}</div>
      ) : (
        <div style={{ marginTop: 2 }}>
          {rows.map((r) => (
            <div key={r.id} className="lc-run">
              <span className={`sl-badge ${statusClass(r.status)}`}>{t("partitioning.st_" + r.status, { defaultValue: r.status })}</span>
              <span className="lc-run-meta">{(r.completedAtUtc || r.startedAtUtc || r.assessedAtUtc || "").replace("T", " ").slice(0, 19)}</span>
              <span className="lc-run-meta">{r.provider}{r.estimatedSizeBytes ? " · " + formatBytes(r.estimatedSizeBytes) : ""}</span>
              {r.errorMessage || r.reason ? (
                <span className="lc-run-meta" style={{ flexBasis: "100%", color: r.errorMessage ? "#b91c1c" : undefined }}>
                  {r.errorMessage || r.reason}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A compact KPI-style stat: small-caps label stacked over a bold value.
function Stat({ k, v }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af" }}>{k}</span>
      <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{v}</span>
    </div>
  );
}
